import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAccountId } from '~publish/contexts/AccountContext'

import { makeNoteSubject, noteSubjectKey } from './noteSubject'
import type { NoteSubject } from './noteSubject'

export type NoteWriteScope = { accountId: string; subject: NoteSubject }

export type ScopedRun<T> = { ran: false } | { ran: true; result: T }

/**
 * The scaffolding every Note write on an existing Note shares. A write is
 * scoped to the account and subject the feed shows:
 *
 * - one request per scope runs at a time;
 * - a result the server confirms while another scope shows is kept, and
 *   applied to the cache once its scope shows again, so it does not land in
 *   the wrong feed.
 *
 * `applyConfirmed` must be stable, since a new one re-runs the deferred apply.
 */
export function useScopedNoteWrite<Confirmed>(
  subject: NoteSubject,
  applyConfirmed: (confirmed: Confirmed) => void,
): {
  scope: NoteWriteScope | null
  scopeKey: string | null
  /** Whether the feed still shows the scope a write started in. */
  isCurrent: (key: string) => boolean
  /** A write is running, or confirmed and waiting for its scope, here. */
  pending: boolean
  /** Runs the write unless another one is running in the same scope. */
  run: <T>(key: string, write: () => Promise<T>) => Promise<ScopedRun<T>>
  /** Keeps a confirmed result until its scope shows again. */
  keepConfirmed: (key: string, confirmed: Confirmed) => void
} {
  const accountId = useAccountId()
  const { subjectType, subjectId } = subject
  const scope = useMemo(
    (): NoteWriteScope | null =>
      accountId
        ? { accountId, subject: makeNoteSubject(subjectType, subjectId) }
        : null,
    [accountId, subjectId, subjectType],
  )
  const scopeKey = scope ? noteSubjectKey(scope.accountId, scope.subject) : null
  const currentKeyRef = useRef(scopeKey)
  currentKeyRef.current = scopeKey
  const runningKeysRef = useRef<Set<string>>(new Set())
  const [runningKeys, setRunningKeys] = useState<Set<string>>(new Set())
  const [confirmed, setConfirmed] = useState<Record<string, Confirmed>>({})

  useEffect(() => {
    if (!scopeKey || !(scopeKey in confirmed)) return
    const waiting = confirmed[scopeKey]
    applyConfirmed(waiting)
    setConfirmed((current) => {
      if (current[scopeKey] !== waiting) return current
      const next = { ...current }
      delete next[scopeKey]
      return next
    })
  }, [applyConfirmed, confirmed, scopeKey])

  const isCurrent = useCallback(
    (key: string): boolean => currentKeyRef.current === key,
    [],
  )

  const run = useCallback(
    async <T>(key: string, write: () => Promise<T>): Promise<ScopedRun<T>> => {
      if (runningKeysRef.current.has(key)) return { ran: false }
      runningKeysRef.current.add(key)
      setRunningKeys((current) => new Set(current).add(key))
      try {
        return { ran: true, result: await write() }
      } finally {
        runningKeysRef.current.delete(key)
        setRunningKeys((current) => {
          const next = new Set(current)
          next.delete(key)
          return next
        })
      }
    },
    [],
  )

  const keepConfirmed = useCallback((key: string, result: Confirmed): void => {
    setConfirmed((current) => ({ ...current, [key]: result }))
  }, [])

  return {
    scope,
    scopeKey,
    isCurrent,
    pending: scopeKey
      ? runningKeys.has(scopeKey) || scopeKey in confirmed
      : false,
    run,
    keepConfirmed,
  }
}
